import produce from 'immer';

import {
  GenericStructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import TableUtil from 'notebook/tables/TableUtil';

interface HasRowsAndColumns<TRowMetadata> {
  columns: Array<GenericStructuredTableColumn<unknown>>;
  rows: Array<StructuredTableRow<TRowMetadata>>;
}

/** Re-merges a Structured Table's single input columns. Call after adding rows to a table.
 *
 * This is very similar to remergeSingleInputColumns in TableUtil except that it operates directly on a
 * StructuredTable rather than a Table.
 */
export default function remergeStructuredTableSingleInputs<T extends HasRowsAndColumns<unknown>>(
  structuredTable: T
): T {
  const singleInputIndexRange = TableUtil.getSingleInputIndexRange(
    structuredTable.columns as Parameters<typeof TableUtil.getSingleInputIndexRange>[0]
  );
  // if there are no single input columns, return the table as is
  if (!singleInputIndexRange) {
    return structuredTable;
  }
  const [sliceStart, sliceEnd] = singleInputIndexRange;
  return produce(structuredTable, (draft) => {
    const {rows} = draft;
    for (const [rowIndex, row] of rows.entries()) {
      for (let columnIndex = sliceStart; columnIndex <= sliceEnd; columnIndex++) {
        const cell = row.cells[columnIndex];
        // if it's the first row, expand the merge to the full table
        if (rowIndex === 0) {
          cell.mergeRowSpan = rows.length;
          cell.mergeColSpan = 1;
          // for all other rows, clear the cell
        } else {
          row.cells[columnIndex] = TableUtil.makeEmptyCell();
        }
      }
    }
  });
}
